import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AuthDisabledBanner from './components/common/AuthDisabledBanner';
// TRAC-959: /embed is the customer path — it stays a static import so the entry
// bundle IS the chat, with no second fetch before it can paint.
import EmbedPage from './pages/EmbedPage';
// Every other route is internal (admin, QA, test harnesses) or rarely hit, and
// lazy-loading them keeps their weight — recharts, the dashboards, ~10 test
// pages — out of the bundle every tirerack.com page view downloads.
// The deploy syncs assets with --delete, so a page loaded before a deploy can
// ask for a chunk hash that no longer exists. One reload picks up the new
// index.html; the session flag keeps a genuinely broken chunk from looping.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LazyModule = { default: React.ComponentType<any> };
const RELOADED_KEY = '__traci_chunk_reloaded';
const lazyPage = (load: () => Promise<LazyModule>) =>
  React.lazy(() =>
    load()
      .then((m) => { try { sessionStorage.removeItem(RELOADED_KEY); } catch { /* storage off */ } return m; })
      .catch((err) => {
        let reloaded = false;
        try { reloaded = sessionStorage.getItem(RELOADED_KEY) === '1'; } catch { /* storage off */ }
        if (!reloaded) {
          try { sessionStorage.setItem(RELOADED_KEY, '1'); } catch { /* storage off */ }
          window.location.reload();
        }
        throw err;
      })
  );
const Login = lazyPage(() => import('./pages/Login'));
const Callback = lazyPage(() => import('./pages/Callback'));
const Blocked = lazyPage(() => import('./pages/Blocked'));
const SignedOut = lazyPage(() => import('./pages/SignedOut'));
const AdminDashboard = lazyPage(() => import('./pages/AdminDashboard'));
const PresencePage = lazyPage(() => import('./pages/PresencePage'));
const FunnelPage = lazyPage(() => import('./pages/FunnelPage'));
const GlossaryPage = lazyPage(() => import('./pages/GlossaryPage'));
const ChatWidget = lazyPage(() => import('./components/chat/ChatWidget'));
const GridlifePage = lazyPage(() => import('./pages/GridlifePage'));
const AnalyticsTest = lazyPage(() => import('./pages/AnalyticsTest'));
const TDGTest = lazyPage(() => import('./pages/TDGTest'));
const TestingHub = lazyPage(() => import('./pages/TestingHub'));
const InternalMarker = lazyPage(() => import('./pages/InternalMarker'));
const ConnectPoc = lazyPage(() => import('./pages/ConnectPoc'));
const EmbedGatingTest = lazyPage(() => import('./pages/EmbedGatingTest'));
const TRMockTest = lazyPage(() => import('./pages/TRMockTest'));
const EmbedTimingTest = lazyPage(() => import('./pages/EmbedTimingTest'));
const QADashboard = lazyPage(() => import('./pages/QADashboard'));
const ContextDebug = lazyPage(() => import('./pages/ContextDebug'));
const CardAudit = lazyPage(() => import('./pages/CardAudit'));
import { usePageTracking } from './hooks/usePageTracking';

// Component to handle page tracking within Router context
function PageTracker(): null {
  usePageTracking();
  return null;
}

function App(): React.ReactElement {
  return (
    <Router>
      <PageTracker />
      <AuthProvider>
        {/* TRAC-478: Unified dev-info pill. Only render on non-/embed routes so we
            don't get two pills stacked when the portal root (/) iframes /embed —
            both frames are same-origin and would each try to render. The outer
            frame reads the same auth/experiment/region data via shared localStorage
            and /health, so it has everything the iframe pill would have shown. */}
        {!window.location.pathname.startsWith('/embed') &&
          !window.location.pathname.startsWith('/gridlife') && <AuthDisabledBanner />}
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              zIndex: 10001,
              pointerEvents: 'auto' as const,
            },
            error: {
              style: {
                background: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                pointerEvents: 'auto' as const,
              },
              iconTheme: {
                primary: '#dc2626',
                secondary: '#fef2f2',
              },
            },
          }}
          containerStyle={{
            zIndex: 10001,
            pointerEvents: 'none',
          }}
        />
        <div className="App">
          {/* Lazy routes suspend until their chunk arrives, so the fallback is
              what a slow connection sees — keep it a real (if minimal) loading
              state. /embed never hits this — EmbedPage is static. */}
          <Suspense
            fallback={
              <div role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#6b7280', font: '14px -apple-system, BlinkMacSystemFont, sans-serif' }}>
                Loading…
              </div>
            }
          >
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/callback" element={<Callback />} />
            <Route path="/signed-out" element={<SignedOut />} />
            {/* TRACI-474: sign-up and password reset are gone; Styx owns identity. */}
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
            <Route path="/blocked" element={<Blocked />} />

            {/* TR.com embed route - uses API key auth, not Cognito */}
            <Route path="/embed" element={<EmbedPage />} />

            {/* TRAC-757: Public unauthed chat for the GRIDLIFE event.
                Auto-mints a public embed token (source=gridlife), no login. */}
            <Route path="/gridlife" element={<GridlifePage />} />

            {/* Analytics test page - for testing Adobe Analytics events */}
            <Route path="/analytics-test" element={<AnalyticsTest />} />

            {/* TDG test page - for testing TDG link card (TRAC-117) */}
            <Route path="/tdg-test" element={<TDGTest />} />

            {/* TR.com mock test - side-panel integration test (TRAC-357) */}
            <Route path="/tr-test" element={<TRMockTest />} />

            {/* Embed timing test - scriptable parent for TRAC-694 timing paths */}
            <Route path="/embed-timing-test" element={<EmbedTimingTest />} />

            {/* Context debug - shows hidden context injected into agent messages (TRAC-416) */}
            <Route path="/context-debug" element={<ContextDebug />} />

            {/* Card render audit - every emitted UI card x variation through the real renderer */}
            <Route path="/card-audit" element={<CardAudit />} />

            {/* Testing hub - central navigation for all test harnesses */}
            <Route path="/testing" element={<TestingHub />} />

            {/* Embed gating diagnostic (TRAC-604) */}
            <Route path="/embed-gating-test" element={<EmbedGatingTest />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ChatWidget />
                </ProtectedRoute>
              }
            />

            {/* Mark this browser as a team member's, so our own testing stops
                counting as customer traffic (TRAC-946) */}
            <Route
              path="/internal-marker"
              element={
                <ProtectedRoute requireAdminOrQA={true}>
                  <InternalMarker />
                </ProtectedRoute>
              }
            />

            {/* "Send my chats to Connect" (CPOC-100): any signed-in person; the backend
                decides who sees the switch. Moved out of Dev Settings. */}
            <Route
              path="/connect-poc"
              element={
                <ProtectedRoute>
                  <ConnectPoc />
                </ProtectedRoute>
              }
            />

            {/* QA routes */}
            <Route
              path="/qa/*"
              element={
                <ProtectedRoute requireAdminOrQA={true}>
                  <QADashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Glossary — the long form of every term on the analytics tabs (TRAC-929) */}
            <Route
              path="/admin/glossary"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <GlossaryPage />
                </ProtectedRoute>
              }
            />

            {/* Chat Presence — its own top-level destination (TRAC-790) */}
            <Route
              path="/presence"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <PresencePage />
                </ProtectedRoute>
              }
            />

            {/* Conversion funnel readout + page script switches (TRACI-632) */}
            <Route
              path="/analytics/funnel"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <FunnelPage />
                </ProtectedRoute>
              }
            />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;

